import { BYPASSES_CUTOFF, EddConfig, getEddConfig } from '@/data/config/eddConfig';
import { TYPES } from '@/data/constants/product';
import { useDefaultStore } from '@/data/Content/DefaultStore';
import { useLeadTime } from '@/data/Content/LeadTime';
import { useSettings } from '@/data/Settings';
import { ProductType } from '@/data/types/Product';
import { calculateEddDate, isPastCutoff } from '@/data/utils/eddDateCalculation';
import { ComboStock, getComboStock, getPickupAvailability } from '@/data/utils/eddInventory';
import { isDeliveryOnlyInStore } from '@/data/utils/eddProductFlags';
import { getProductMinQuantity } from '@/data/utils/getProductMinQuantity';
import { useIsClient } from '@/data/utils/useIsClient';
import { useMemo } from 'react';
import { fulfillmentTypes } from 'shared/custom/constants/commonConstants';
import { getFulfillmentType } from 'shared/custom/utils/inventoryUtils';

export type PickupDateState =
	/**
	 * Still resolving — distinct from `no-label`, which is a settled "render nothing".
	 * The label shows a skeleton for this so the customer never sees partial text
	 * before the real result lands. Only reachable before hydration (AC25).
	 */
	| { status: 'pending' }
	| { status: 'no-label' }
	| { status: 'not-available' }
	| { status: 'available-no-date' }
	| {
			status: 'with-date';
			descriptor: 'today' | 'tomorrow' | 'date';
			formatted: string;
			deliveryDate: Date;
	  };

const STORECONF_ENABLED_BY_TYPE: Record<string, keyof EddConfig> = {
	[fulfillmentTypes.DROPSHIP]: 'pickupDropshipEnabled',
	[fulfillmentTypes.SPECIAL_ORDER]: 'pickupSpecialOrderEnabled',
	[fulfillmentTypes.ONLINE_ONLY]: 'pickupOnlineOnlyEnabled',
};

const asDateState = (result: ReturnType<typeof calculateEddDate>): PickupDateState =>
	result
		? {
				status: 'with-date',
				descriptor: result.descriptor,
				formatted: result.formatted,
				deliveryDate: result.date,
		  }
		: { status: 'no-label' };

/**
 * Step 2 (AC5-AC6): Dropship / Special Order / Online Only, gated by their storeconf
 * flag. These resolve from x_itemleadtime — how long the SKU takes to reach the
 * store — with the pickup cutoff then adding its own express/standard penalty on
 * top. Market stock is still required before a date is promised.
 *
 * This is a deliberate deviation from 158621 AC12/AC14 and 159042 AC6-AC7, which
 * reserve the leadtime for Delivery and route these types to step 3. Product
 * confirmed the leadtime is the intended source here — do not "correct" it back.
 */
const resolveEnabledFulfillmentType = (
	config: EddConfig,
	enabledKey: keyof EddConfig,
	hasMarketAvailability: boolean,
	leadTime: number | undefined,
	fulfillmentType: string | undefined,
	allowNoDateWithoutLeadTime: boolean
): PickupDateState => {
	if (!hasMarketAvailability) {
		return { status: 'no-label' };
	}
	if (!config[enabledKey]) {
		return allowNoDateWithoutLeadTime ? { status: 'not-available' } : { status: 'no-label' };
	}
	if (leadTime === undefined) {
		return allowNoDateWithoutLeadTime ? { status: 'available-no-date' } : { status: 'no-label' };
	}
	if (fulfillmentType && BYPASSES_CUTOFF.has(fulfillmentType)) {
		return asDateState(
			calculateEddDate({ daysToAdd: leadTime, channel: 'pickup', skipExclusions: true }, config)
		);
	}
	const daysToAdd =
		leadTime +
		(isPastCutoff(config.pickupCutoffTime) ? config.pickupStandardDays : config.pickupExpressDays);
	return asDateState(calculateEddDate({ daysToAdd, channel: 'pickup' }, config));
};

const resolveByPickupCutoff = (config: EddConfig): PickupDateState => {
	const daysToAdd = isPastCutoff(config.pickupCutoffTime)
		? config.pickupStandardDays
		: config.pickupExpressDays;
	return asDateState(calculateEddDate({ daysToAdd, channel: 'pickup' }, config));
};

/** Step 3 (AC7): remaining fulfillment types (e.g. Store). */
const resolveRemainingFulfillmentTypes = (
	config: EddConfig,
	hasStoreAvailability: boolean
): PickupDateState =>
	hasStoreAvailability ? resolveByPickupCutoff(config) : { status: 'no-label' };

/**
 * Combos (TYPES.kit) resolve from their components' inventory rather than from the
 * parent's own stock or lead time: every component in the selected store gives a
 * date, every component somewhere in the market gives the no-date state, and a
 * component missing from both means no label. See LeadTime/Combos.md.
 *
 * Takes the already-resolved `ComboStock` rather than components, so this single
 * decision is shared by every surface regardless of how that stock was derived —
 * the PLP derives it per-component from the index (`getComboStock` below), the PDP
 * derives it from the already-aggregated per-kit availability the page's inventory
 * call returns (see `usePdpPickupOption`).
 */
const resolveComboPickupState = (config: EddConfig, stock: ComboStock): PickupDateState => {
	if (stock === 'incomplete') return { status: 'no-label' };
	if (stock === 'all-in-market') return { status: 'available-no-date' };
	return resolveByPickupCutoff(config);
};

/**
 * AC6 of the PDP delivery-options story: whether the given fulfillment type is one
 * of the BOSS types (Dropship / Special Order / Online Only) AND its storeconf is
 * currently enabled — the same gate Step 2 above already applies, exposed here so
 * the PDP's counter (AC5/AC6) can read it without re-deriving Step 2's own logic.
 */
export const isBossFulfillmentType = (
	config: EddConfig,
	fulfillmentType: string | undefined
): boolean => {
	const enabledKey = fulfillmentType ? STORECONF_ENABLED_BY_TYPE[fulfillmentType] : undefined;
	return !!enabledKey && !!config[enabledKey];
};

export type PickupResolutionInputs = {
	isDeliveryOnly: boolean;
	fulfillmentType: string | undefined;
	isCombo: boolean;
	/** Only meaningful when `isCombo` is true. */
	comboStock: ComboStock | undefined;
	leadTime: number | undefined;
	minQuantity: number;
	/** `undefined` means the level is unknown, not that it's zero — see call sites. */
	storeAvailability: number | undefined;
	marketAvailability: number | undefined;
	allowNoDateWithoutLeadTime?: boolean;
};

/**
 * The full store-pickup eligibility tree (story 159042 AC3-AC7), independent of
 * where its inputs came from. Story 158621/159042's PLP hook (`usePickupDate`
 * below) and the PDP delivery-options story's `usePdpPickupOption` both call this
 * exact function — the tree is reused as-is, never duplicated or forked, only the
 * derivation of these inputs differs by surface.
 */
// eslint-disable-next-line complexity
export const resolvePickupDateState = (
	config: EddConfig,
	inputs: PickupResolutionInputs
): PickupDateState => {
	// Step 1 (AC4) — deliveryOnly short-circuits everything below.
	if (inputs.isDeliveryOnly) {
		return { status: 'not-available' };
	}

	// Combos are evaluated before the fulfillment-type tree: their pickup date comes
	// from the components (or, on the PDP, from the already-aggregated per-kit
	// counters), so a combo tagged Dropship or Special Order must not fall into the
	// lead-time branch below.
	if (inputs.isCombo) {
		return resolveComboPickupState(config, inputs.comboStock ?? 'incomplete');
	}

	const hasMarketAvailability =
		inputs.marketAvailability === undefined
			? true
			: inputs.marketAvailability >= inputs.minQuantity;
	const hasStoreAvailability =
		inputs.storeAvailability === undefined ? true : inputs.storeAvailability >= inputs.minQuantity;

	const enabledKey = inputs.fulfillmentType
		? STORECONF_ENABLED_BY_TYPE[inputs.fulfillmentType]
		: undefined;
	return enabledKey
		? resolveEnabledFulfillmentType(
				config,
				enabledKey,
				hasMarketAvailability,
				inputs.leadTime,
				inputs.fulfillmentType,
				inputs.allowNoDateWithoutLeadTime ?? false
		  )
		: resolveRemainingFulfillmentTypes(config, hasStoreAvailability);
};

/**
 * Store-pickup resolution for the PLP EDD label (story 159042 AC3-AC7, three-step
 * tree). Both inventory levels come from the flat `inventories.*` keys the listing
 * payload already carries, so no call is made per product (AC9 of story 158621).
 * A level the index omits entirely stays `undefined` and is treated as unknown
 * ("assume available"), so a missing projection degrades to the previous label
 * rather than blanking it; an explicit zero is real and resolves per AC7.
 *
 * AC24/AC25/AC28: resolution must happen on the client at hydration, never
 * server-rendered/cached — see `useHomeDeliveryDate` for the same guard.
 */
export const usePickupDate = (
	product: ProductType | undefined,
	physicalStoreId: string
): PickupDateState => {
	const isClient = useIsClient();
	const leadTime = useLeadTime(product, physicalStoreId);
	const { settings } = useSettings();

	const fulfillmentType = useMemo(
		() =>
			getFulfillmentType(
				product?.attributes,
				(product?.ribbons ?? []).map((ribbon) => ribbon.identifier)
			),
		[product?.attributes, product?.ribbons]
	);

	// The store-level inventory key is the physical store's uniqueId, not the
	// stLocId that `physicalStoreId` carries — read from the geo-location cookie
	// so it is available on first render, with no per-card store lookup.
	const { uniqueId } = useDefaultStore();

	const { storeAvailability, marketAvailability } = useMemo(
		() => getPickupAvailability(product, { storeUniqueId: uniqueId, physicalStoreId }),
		[product, uniqueId, physicalStoreId]
	);

	return useMemo(() => {
		// AC25: nothing is resolved server-side. Reported as `pending` rather than
		// `no-label` so the label can show a skeleton instead of an empty row.
		if (!isClient) {
			return { status: 'pending' };
		}

		const config = getEddConfig(settings.userData);
		const isCombo = !!product && product.type === TYPES.kit;
		const comboStock = isCombo
			? getComboStock(product?.components ?? [], { storeUniqueId: uniqueId, physicalStoreId })
			: undefined;

		// LeadTime/NominalQuantity.md: a regular SKU sold in multiples needs at least
		// its minimum quantity at a level to count as available there. Anything that
		// is not a real multiple (1, absent, unparseable) keeps the plain "> 0" rule.
		// Computed unconditionally; `resolvePickupDateState` only consumes it on the
		// non-combo path.
		const nominalQuantity = product ? getProductMinQuantity(product) : 1;
		const minQuantity =
			Number.isFinite(nominalQuantity) && nominalQuantity > 1 ? nominalQuantity : 1;

		return resolvePickupDateState(config, {
			// Step 1 (AC4) — deliveryOnly is the DROPSHIP_DISABLED_PRODUCTS attribute (every
			// store) OR the selected store's x_itemdeliveryonly flag (163615).
			isDeliveryOnly: isDeliveryOnlyInStore(product, physicalStoreId),
			fulfillmentType,
			isCombo,
			comboStock,
			leadTime,
			minQuantity,
			storeAvailability,
			marketAvailability,
		});
	}, [
		isClient,
		product,
		settings.userData,
		marketAvailability,
		storeAvailability,
		fulfillmentType,
		leadTime,
		uniqueId,
		physicalStoreId,
	]);
};
